import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useGetDatasetByIdSchema } from "@/api/dataset/useDatasetAPI";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { DataSettings, EDataSettingSpatialType } from "@/types/dataSetting";

import AttributeSelection from "./AttributeSelection";
import { MULTIPLE_GEOMETRY_OPTIONS } from "./dataSettingOptions";

interface MultipleGeometrySettingProps {
  dataSetting: DataSettings | null;
  handleSpatialAggregationSelect: <
    K extends keyof NonNullable<DataSettings["spatialAggregation"]>
  >(
    key: K,
    value: NonNullable<DataSettings["spatialAggregation"]>[K]
  ) => void;
}

const MultipleGeometrySetting = ({
  dataSetting,
  handleSpatialAggregationSelect
}: MultipleGeometrySettingProps) => {
  const { id } = useParams();

  const geometryType = useMemo(() => {
    // value of dataSetting?.spatialAggregation?.type is 'poi' or 'polygon'
    // But, for api, we need to convert it to 'point' or 'polygon'
    const currentGeometryType = dataSetting?.spatialAggregation?.type;
    if (!currentGeometryType) return "";
    if (currentGeometryType.value === EDataSettingSpatialType.Point) {
      return "point";
    }
    return dataSetting?.spatialAggregation?.type?.value as string;
  }, [dataSetting?.spatialAggregation?.type]);

  const { data: attributesGeom, isLoading } = useGetDatasetByIdSchema({
    datasetId: id,
    geometryType
  });

  const attributesGeomMapping: IOptionItem[] = useMemo(() => {
    if (!attributesGeom) return [];
    return attributesGeom?.schemas?.map((attr) => ({
      label: attr.attribute_name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: attr.id,
      description: attr.attribute_name
    }));
  }, [attributesGeom]);

  return (
    <div className="flex flex-col">
      <Dropdown
        className="mt-4"
        placeholder="Spatial Aggregation*"
        data={MULTIPLE_GEOMETRY_OPTIONS}
        value={
          dataSetting?.spatialAggregation?.type?.value
            ? dataSetting?.spatialAggregation?.type
            : null
        }
        onSelect={(data) => {
          if (!data) return;
          handleSpatialAggregationSelect("type", data);
          handleSpatialAggregationSelect("pointAttribute", undefined);
          handleSpatialAggregationSelect("polygonAttribute", undefined);
        }}
      />
      {dataSetting?.spatialAggregation?.type?.value ===
        EDataSettingSpatialType.Point && (
        <AttributeSelection
          label="Attribute Selection"
          description="Select one attribute for geocoding to get the geometric value of the Point."
          items={attributesGeomMapping}
          isLoading={isLoading}
          selectedValue={dataSetting.spatialAggregation?.pointAttribute ?? ""}
          onSelect={(value) =>
            handleSpatialAggregationSelect("pointAttribute", value)
          }
        />
      )}
      {dataSetting?.spatialAggregation?.type?.value ===
        EDataSettingSpatialType.Polygon && (
        <AttributeSelection
          label="Attribute Selection"
          description="Please select one attribute for geocoding to get the geometric value of the Polygon."
          items={attributesGeomMapping}
          isLoading={isLoading}
          selectedValue={dataSetting.spatialAggregation?.polygonAttribute ?? ""}
          onSelect={(value) =>
            handleSpatialAggregationSelect("polygonAttribute", value)
          }
        />
      )}
    </div>
  );
};

export default MultipleGeometrySetting;
